import {
  ACTION_STATUS_LABELS,
  ALERT_CRITICALITY_LABELS,
  ALERT_STATUS_LABELS,
  ALERT_TYPE_LABELS,
  DRILL_LIST_LIMIT,
  drillUnitSchema,
  formatDate,
  formatDateTime,
  targetPeriodLabel,
  TRAFFIC_LIGHT_LABELS,
  type DrillUnit,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowRight, Paperclip } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { useAuth, useCan } from '@/lib/auth/session';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const withUnit = (v: number | null, unit: string) =>
  v === null ? '—' : unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const pct = (v: number | null) => (v === null ? null : `${formatValue(v)}%`);
const monthLabel = (iso: string) =>
  `${targetPeriodLabel('MONTH', Number(iso.slice(5, 7)))}/${iso.slice(0, 4)}`;

/**
 * A causa na unidade (T-144b, RF-37, PRD §11.24; DEC-058): o KPI numa unidade no mês — realizado, meta e
 * performance —, os resultados lançados com a justificativa, os desvios e as ações ligadas a eles, com links.
 */
export function UnitCause({
  organizationId,
  projectId,
  kpiId,
  businessUnitId,
  year,
  month,
}: {
  organizationId: string;
  projectId: string;
  kpiId: string;
  businessUnitId: string;
  year: number;
  month: number;
}) {
  const { client } = useAuth();
  const query = useQuery({
    queryKey: ['drill-unit', projectId, kpiId, businessUnitId, year, month],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/drill-down/unit`, {
        query: { kpiId, businessUnitId, year: String(year), month: String(month) },
        schema: drillUnitSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  if (query.isPending) return <LoadingState rows={4} label="Carregando a unidade" />;
  if (query.isError && !query.data) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
    );
  }
  const d = query.data;
  const label = `${targetPeriodLabel('MONTH', d.month)}/${d.year}`;
  return (
    <section
      aria-label={`${d.unit.name} em ${label}`}
      aria-busy={query.isFetching}
      className="flex flex-col gap-4 rounded-lg border border-border-strong bg-surface p-4"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-bold">
          {d.unit.name}
          {d.unit.archived ? ' (arquivada)' : ''} · {d.kpi.code} · {d.kpi.name}
        </h2>
        <p className="text-sm text-muted-foreground">O que explica o número da unidade em {label}.</p>
      </header>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure label="Realizado">{withUnit(d.current.actual, d.kpi.unitLabel)}</Figure>
        <Figure label="Meta">{withUnit(d.current.target, d.kpi.unitLabel)}</Figure>
        <Figure label="Performance do mês">
          <StatusBadge
            status={d.current.light}
            label={pct(d.current.performance) ?? TRAFFIC_LIGHT_LABELS[d.current.light]}
          />
          {d.current.performance !== null ? (
            <span className="sr-only">, {TRAFFIC_LIGHT_LABELS[d.current.light]}</span>
          ) : null}
        </Figure>
        <Figure label="Acumulado do ano">
          <span className="block">
            {withUnit(d.current.ytdActual, d.kpi.unitLabel)} de{' '}
            {withUnit(d.current.ytdTarget, d.kpi.unitLabel)}
          </span>
          <StatusBadge
            status={d.current.ytdLight}
            label={pct(d.current.ytdPerformance) ?? TRAFFIC_LIGHT_LABELS[d.current.ytdLight]}
          />
          {d.current.ytdPerformance !== null ? (
            <span className="sr-only">, {TRAFFIC_LIGHT_LABELS[d.current.ytdLight]}</span>
          ) : null}
        </Figure>
      </dl>
      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
        <Results d={d} />
        <Deviations d={d} organizationId={organizationId} />
        <Actions d={d} organizationId={organizationId} />
      </div>
    </section>
  );
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

function Block({ title, total, children }: { title: string; total: number; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-2">
      <h3 className="flex items-center justify-between gap-2 text-sm font-semibold">
        {title}
        <span className="rounded-full bg-muted px-2 text-xs font-medium tabular-nums">{total}</span>
      </h3>
      {children}
    </section>
  );
}

function More({ total, shown }: { total: number; shown: number }) {
  return total > shown ? (
    <p className="text-xs text-muted-foreground">
      Mostrando {shown} de {total} (os primeiros {DRILL_LIST_LIMIT}).
    </p>
  ) : null;
}

function Results({ d }: { d: DrillUnit }) {
  return (
    <Block title="Resultados e justificativas" total={d.results.length}>
      {d.results.length ? (
        <ul aria-label="Resultados e justificativas" className="flex flex-col divide-y divide-border">
          {d.results.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 py-2 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{monthLabel(r.periodStart)}</span>
                <span className="font-semibold tabular-nums">{withUnit(r.value, d.kpi.unitLabel)}</span>
              </div>
              <p className={r.justification ? 'break-words' : 'text-muted-foreground'}>
                {r.justification ?? 'Sem justificativa.'}
              </p>
              {r.evidenceName ? (
                <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Paperclip aria-hidden className="size-3.5" />
                  Evidência: {r.evidenceName}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {r.createdByName} · {formatDateTime(r.createdAt)}
                {r.version > 1 ? ` · versão ${r.version}${r.reason ? ` (correção: ${r.reason})` : ''}` : ''}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhum resultado lançado na unidade neste ano até o mês.
        </p>
      )}
    </Block>
  );
}

function Deviations({ d, organizationId }: { d: DrillUnit; organizationId: string }) {
  const can = useCan();
  if (!d.deviations) return null;
  const b = d.deviations;
  return (
    <Block title="Desvios" total={b.total}>
      {b.items.length ? (
        <ul aria-label="Desvios" className="flex flex-col divide-y divide-border">
          {b.items.map((a) => {
            const body = (
              <>
                <span className="block font-medium break-words">{a.title}</span>
                <span className="block text-xs break-words text-muted-foreground">{a.message}</span>
                <span className="block text-xs text-muted-foreground">
                  {ALERT_TYPE_LABELS[a.type]} · {ALERT_CRITICALITY_LABELS[a.criticality]} ·{' '}
                  {ALERT_STATUS_LABELS[a.status]} · {formatDate(a.periodEnd)} ·{' '}
                  {a.atUnit ? 'nesta unidade' : 'do indicador todo'}
                  {a.responsible ? ` · ${a.responsible}` : ''}
                </span>
                {a.resolution ? (
                  <span className="block text-xs text-muted-foreground">Encerramento: {a.resolution}</span>
                ) : null}
              </>
            );
            return (
              <li key={a.id} className="py-2 text-sm">
                {can('alertas', 'view') ? (
                  <Link
                    to={`/empresas/${organizationId}/gestao/alertas?projeto=${d.project.id}&alerta=${a.id}`}
                    className="flex items-start justify-between gap-2 rounded hover:bg-muted/50"
                  >
                    <span className="min-w-0">{body}</span>
                    <ArrowRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  </Link>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhum desvio do indicador nesta unidade no ano até o mês.
        </p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Block>
  );
}

function Actions({ d, organizationId }: { d: DrillUnit; organizationId: string }) {
  if (!d.actions) return null;
  const b = d.actions;
  return (
    <Block title="Ações" total={b.total}>
      {b.items.length ? (
        <ul aria-label="Ações" className="flex flex-col divide-y divide-border">
          {b.items.map((a) => (
            <li key={a.id} className="py-2 text-sm">
              <Link
                to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${d.project.id}&plano=${a.plan.id}&acao=${a.id}`}
                className="flex items-start justify-between gap-2 rounded hover:bg-muted/50"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 font-medium break-words">
                    {a.late ? (
                      <span aria-hidden className="size-2 shrink-0 rounded-full bg-status-red-solid" />
                    ) : null}
                    {a.title}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {ACTION_STATUS_LABELS[a.status]}
                    {a.dueDate ? ` · prazo ${formatDate(a.dueDate)}${a.late ? ' (atrasada)' : ''}` : ''}
                    {a.responsible ? ` · ${a.responsible}` : ''} · Plano: {a.plan.name}
                  </span>
                </span>
                <ArrowRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhuma ação ligada aos desvios ou às iniciativas na unidade.
        </p>
      )}
      <More total={b.total} shown={b.items.length} />
    </Block>
  );
}
